export interface StorageData {
  jsonContent: string
  timestamp: number
  version: string
}

export interface ThemeData {
  isDarkMode: boolean
  timestamp: number
  version: string
}

export interface StorageLoadResult {
  success: boolean
  hasStoredData: boolean
  data?: string
  error?: string
}

export class LocalStorageService {
  private static readonly STORAGE_KEY = 'jsonbox_editor_data'
  private static readonly THEME_KEY = 'jsonbox_theme_preference'
  private static readonly VERSION = '1.0.0'
  private static readonly MAX_SIZE_BYTES = 4 * 1024 * 1024 // 4MB limit for safety

  /**
   * Save JSON content to localStorage with compression
   */
  static saveJsonContent(jsonContent: string): { success: boolean; error?: string } {
    try {
      const data: StorageData = {
        jsonContent,
        timestamp: Date.now(),
        version: this.VERSION
      }

      // Compress the data to save space
      const compressed = this.compressData(data)
      
      // Check if compressed data exceeds storage limit
      if (compressed.length > this.MAX_SIZE_BYTES) {
        return { 
          success: false, 
          error: `JSON content is too large (${this.formatBytes(compressed.length)}). Please reduce the size to continue.` 
        }
      }

      localStorage.setItem(this.STORAGE_KEY, compressed)
      return { success: true }
    } catch (error) {
      console.error('Failed to save to localStorage:', error)
      return { 
        success: false, 
        error: 'Failed to save content. Your browser may have limited storage or be in private mode.' 
      }
    }
  }

  /**
   * Load JSON content from localStorage
   */
  static loadJsonContent(): StorageLoadResult {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      if (stored === null) {
        return { success: true, hasStoredData: false, data: '' }
      }

      const data: StorageData = this.decompressData(stored)
      
      // Validate stored data
      if (!data || typeof data.jsonContent !== 'string') {
        this.clearStorage()
        return { success: false, hasStoredData: false, error: 'Stored data is corrupted. Storage has been cleared.' }
      }

      // Check version compatibility
      if (data.version !== this.VERSION) {
        // For future version migrations
        console.warn('Stored data version mismatch, but continuing...')
      }

      return { success: true, hasStoredData: true, data: data.jsonContent }
    } catch (error) {
      console.error('Failed to load from localStorage:', error)
      this.clearStorage()
      return { 
        success: false, 
        hasStoredData: false,
        error: 'Failed to load saved content. Storage has been cleared.' 
      }
    }
  }

  /**
   * Clear stored data
   */
  static clearStorage(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY)
    } catch (error) {
      console.error('Failed to clear localStorage:', error)
    }
  }

  /**
   * Clear all stored data including theme preference
   */
  static clearAllStorage(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY)
      localStorage.removeItem(this.THEME_KEY)
    } catch (error) {
      console.error('Failed to clear all localStorage:', error)
    }
  }

  /**
   * Get storage usage information
   */
  static getStorageInfo(): { used: number; available: number; percentage: number } {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      const used = stored ? stored.length : 0
      const available = this.MAX_SIZE_BYTES
      const percentage = Math.round((used / available) * 100)
      
      return { used, available, percentage }
    } catch {
      return { used: 0, available: this.MAX_SIZE_BYTES, percentage: 0 }
    }
  }

  /**
   * Check if localStorage is available and working
   */
  static isAvailable(): boolean {
    try {
      const testKey = '__test__'
      localStorage.setItem(testKey, 'test')
      localStorage.removeItem(testKey)
      return true
    } catch {
      return false
    }
  }

  /**
   * Simple compression using JSON.stringify with minimal whitespace
   */
  private static compressData(data: StorageData): string {
    return JSON.stringify(data)
  }

  /**
   * Decompress data from storage
   */
  private static decompressData(compressed: string): StorageData {
    return JSON.parse(compressed)
  }

  /**
   * Format bytes to human readable format
   */
  private static formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  /**
   * Save theme preference to localStorage
   */
  static saveThemePreference(isDarkMode: boolean): { success: boolean; error?: string } {
    try {
      const data: ThemeData = {
        isDarkMode,
        timestamp: Date.now(),
        version: this.VERSION
      }

      localStorage.setItem(this.THEME_KEY, JSON.stringify(data))
      return { success: true }
    } catch (error) {
      console.error('Failed to save theme preference:', error)
      return { 
        success: false, 
        error: 'Failed to save theme preference.' 
      }
    }
  }

  /**
   * Load theme preference from localStorage
   */
  static loadThemePreference(): { success: boolean; isDarkMode?: boolean; error?: string } {
    try {
      const stored = localStorage.getItem(this.THEME_KEY)
      if (!stored) {
        return { success: true, isDarkMode: true } // Default to dark mode
      }

      const data: ThemeData = JSON.parse(stored)
      
      // Validate stored data
      if (!data || typeof data.isDarkMode !== 'boolean') {
        this.clearThemePreference()
        return { success: true, isDarkMode: true } // Default to dark mode
      }

      // Check version compatibility
      if (data.version !== this.VERSION) {
        // For future version migrations
        console.warn('Stored theme version mismatch, but continuing...')
      }

      return { success: true, isDarkMode: data.isDarkMode }
    } catch (error) {
      console.error('Failed to load theme preference:', error)
      this.clearThemePreference()
      return { success: true, isDarkMode: true } // Default to dark mode
    }
  }

  /**
   * Clear stored theme preference
   */
  static clearThemePreference(): void {
    try {
      localStorage.removeItem(this.THEME_KEY)
    } catch (error) {
      console.error('Failed to clear theme preference:', error)
    }
  }
}
